import { useEffect, useRef, useState, useCallback } from 'react';
import { PriceBadge, type PriceBadgeProps } from '~/common/components/PriceBadge.tsx';
import { Tooltip, TooltipTrigger, TooltipContent } from '~/common/components/ui/tooltip.tsx';
import { Badge } from '~/common/components/ui/badge.tsx';
import { cn } from '~/common/utils/styleUtil.ts';

type BadgeInfo = { type: PriceBadgeProps['type'] };

interface PriceBadgeGroupProps {
  badges: BadgeInfo[];
  size?: NonNullable<PriceBadgeProps['size']>;
  direction?: 'row' | 'wrap';
  className?: string;
}

export function PriceBadgeGroup({ badges, size = 'md', direction = 'row', className }: Readonly<PriceBadgeGroupProps>) {
  if (badges.length === 0) return null;

  if (direction === 'wrap') {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {badges.map((badge, i) => (
          <PriceBadge key={`${badge.type}-${i}`} type={badge.type} size={size} />
        ))}
      </div>
    );
  }

  return <RowLayout badges={badges} size={size} className={className} />;
}

function RowLayout({
  badges,
  size = 'md',
  className
}: Readonly<{ badges: BadgeInfo[]; size?: NonNullable<PriceBadgeProps['size']>; className?: string }>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(badges.length);

  const calculateVisibleCount = useCallback(() => {
    const container = containerRef.current;
    const measureLayer = measureRef.current;
    if (!container || !measureLayer || badges.length === 0) return;

    const containerWidth = container.clientWidth;
    const gap = 8;
    const overflowWidth = overflowRef.current ? overflowRef.current.offsetWidth + gap : 0;
    const badgeElements = measureLayer.children;

    let count = 0;
    for (let i = 0; i < badges.length; i++) {
      const badge = badgeElements[i] as HTMLElement | undefined;
      if (!badge) continue;
      const badgeEnd = badge.offsetLeft + badge.offsetWidth;
      const isLast = i === badges.length - 1;
      const availableWidth = isLast ? containerWidth : containerWidth - overflowWidth;

      if (badgeEnd > availableWidth) break;
      count++;
    }

    setVisibleCount(count);
  }, [badges.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      calculateVisibleCount();
    });

    observer.observe(container);
    calculateVisibleCount();

    return () => observer.disconnect();
  }, [calculateVisibleCount]);

  const hiddenCount = badges.length - visibleCount;
  const hiddenBadges = badges.slice(visibleCount);

  const sizeClasses =
    size === 'sm'
      ? 'text-[10px] leading-none px-2 py-0.5 rounded-[3px]'
      : 'text-xs leading-none px-3 py-1 rounded-[3px]';

  return (
    <div ref={containerRef} className={cn('relative w-full min-w-0 overflow-hidden', className)}>
      {/* Hidden measuring layer — renders all badges in normal flow, not clipped by overflow-hidden */}
      <div
        ref={measureRef}
        aria-hidden
        className="absolute top-0 left-0 flex flex-nowrap items-center gap-2 invisible pointer-events-none whitespace-nowrap"
      >
        {badges.map((badge, i) => (
          <PriceBadge key={`${badge.type}-${i}`} type={badge.type} size={size} />
        ))}
      </div>

      {/* Hidden overflow badge for measurement */}
      <div ref={overflowRef} aria-hidden className="absolute invisible pointer-events-none">
        <Badge className={cn(sizeClasses)}>+{badges.length}</Badge>
      </div>

      {/* Visible badges */}
      <div className="flex flex-nowrap items-center gap-2">
        {badges.slice(0, visibleCount).map((badge, i) => (
          <PriceBadge key={`${badge.type}-${i}`} type={badge.type} size={size} />
        ))}

        {hiddenCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className={cn('cursor-pointer bg-neutral-200 text-black hover:bg-neutral-300', sizeClasses)}>
                +{hiddenCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-60 py-3">
              <div className="inline-flex w-fit flex-wrap gap-1">
                {hiddenBadges.map((badge, i) => (
                  <PriceBadge key={`${badge.type}-${i}`} type={badge.type} size={size} />
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
